import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import AdminLayout from "../components/AdminLayout";
import { BASE_URLS } from "../../config/urls";
import { apiFetch } from "../../utils/api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../styles/admin-supplies.css";
import {BtnSpinner} from "../../components/fields/BtnSpinner";

type PriceRequestAdminDTO = {
    id: number;
    store: string;
    sendCity: string;
    sum: number;
    active: boolean;
    newSum: number | null;
    firstApplyDate: string | null; // yyyy-MM-dd
};

type StoreFilter = "ALL" | "Ozon" | "Wildberries" | "Яндекс Маркет";

function toISODate(d: Date | null): string | null {
    if (!d) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function fromISODate(s: string | null): Date | null {
    if (!s) return null;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
}

const AdminDirectionsPage: React.FC = () => {
    const [items, setItems] = useState<PriceRequestAdminDTO[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const [storeFilter, setStoreFilter] = useState<StoreFilter>("ALL");
    const [query, setQuery] = useState("");

    // локальные правки строк
    const [draftById, setDraftById] = useState<Record<number, Partial<PriceRequestAdminDTO>>>({});
    const [savingId, setSavingId] = useState<number | null>(null);

    const load = async () => {
        setIsLoading(true);
        try {
            const res = await apiFetch(`${BASE_URLS.api}/admin/price-requests`);
            if (!res.ok) throw new Error(`Failed: ${res.status}`);
            const data: PriceRequestAdminDTO[] = await res.json();
            setItems(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
            setItems([]);
            alert("Не удалось загрузить направления");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return items
            .filter((it) => (storeFilter === "ALL" ? true : it.store === storeFilter))
            .filter((it) => {
                if (!q) return true;
                return (
                    it.store.toLowerCase().includes(q) ||
                    it.sendCity.toLowerCase().includes(q)
                );
            });
    }, [items, storeFilter, query]);

    const getRow = (id: number) => {
        const base = items.find((x) => x.id === id);
        if (!base) return null;
        const draft = draftById[id] || {};
        return { ...base, ...draft };
    };

    const patchRow = (id: number, patch: Partial<PriceRequestAdminDTO>) => {
        setDraftById((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
    };

    const isDirty = (id: number) => !!draftById[id];

    const saveRow = async (id: number) => {
        const row = getRow(id);
        if (!row) return;

        // минимальная валидация
        if (row.sum < 0 || (row.newSum != null && row.newSum < 0)) {
            alert("Цена не может быть отрицательной");
            return;
        }
        if ((row.newSum != null && !row.firstApplyDate) || (row.newSum == null && row.firstApplyDate)) {
            alert("Для плановой цены нужно заполнить и новую сумму, и дату применения.");
            return;
        }

        try {
            setSavingId(id);
            const res = await apiFetch(`${BASE_URLS.api}/admin/price-requests/${id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    id: row.id,
                    store: row.store,
                    sendCity: row.sendCity,
                    sum: row.sum,
                    active: row.active,
                    newSum: row.newSum,
                    firstApplyDate: row.firstApplyDate,
                }),
            });
            if (!res.ok) throw new Error(`Save failed: ${res.status}`);
            const updated: PriceRequestAdminDTO = await res.json();

            setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
            setDraftById((prev) => {
                const cp = { ...prev };
                delete cp[id];
                return cp;
            });
        } catch (e) {
            console.error(e);
            alert("Не удалось сохранить направление");
        } finally {
            setSavingId(null);
        }
    };

    const resetRow = (id: number) => {
        setDraftById((prev) => {
            const cp = { ...prev };
            delete cp[id];
            return cp;
        });
    };

    return (
        <AdminLayout>
            <HelmetProvider>
                <Helmet title="Направления и цены" />
            </HelmetProvider>

            <div className="page_content supplies-page">
                <section className="admin-card" style={{ marginTop: 90 }}>
                    <h1 className="admin-heading">Направления и цены</h1>
                    <p className="admin-subtitle">
                        <b>Активные отображаются в калькуляторе</b>
                    </p>
                    <p className="admin-subtitle">
                        Управление направлениями: активность, текущая цена, плановая цена и дата применения.
                    </p>
                    <p className="admin-subtitle" style={{ marginTop: 14 }}>
                        * Плановая цена применяется в 00:00 по Самаре, когда наступит дата применения.
                    </p>
                    <div className="supplies-header">
                        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", width: "100%" }}>
                            <div className="user-type-selector">
                                {(["ALL", "Wildberries", "Ozon", "Яндекс Маркет"] as StoreFilter[]).map((v) => (
                                    <div className="user-type-option" key={v}>
                                        <label className={`user-type-label ${storeFilter === v ? "active" : ""}`}>
                                            <input
                                                type="radio"
                                                name="storeFilter"
                                                value={v}
                                                className="hidden-radio"
                                                checked={storeFilter === v}
                                                onChange={() => setStoreFilter(v)}
                                            />
                                            {v === "ALL" ? "Все" : v}
                                        </label>
                                    </div>
                                ))}
                            </div>

                            <input
                                className="admin-input"
                                style={{ width: 320 }}
                                placeholder="Поиск: склад или город отправки…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="admin-table-wrapper">
                        <table className="admin_table supplies-table">
                            <thead>
                            <tr>
                                <th>Store</th>
                                <th>Город отправки</th>
                                <th>Активно</th>
                                <th>Текущая цена</th>
                                <th>Новая цена</th>
                                <th>Дата применения</th>
                                <th>Действия</th>
                            </tr>
                            </thead>
                            <tbody>
                            {isLoading && (
                                <tr>
                                    <td colSpan={7} className="admin-table-loading">
                                        <span className="admin-spinner" /> Загрузка направлений...
                                    </td>
                                </tr>
                            )}

                            {!isLoading && filtered.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="admin-table-empty">
                                        Ничего не найдено.
                                    </td>
                                </tr>
                            )}

                            {!isLoading &&
                                filtered.map((it) => {
                                    const row = getRow(it.id)!;
                                    const dirty = isDirty(it.id);
                                    const saving = savingId === it.id;

                                    return (
                                        <tr key={it.id}>
                                            <td>{row.store}</td>
                                            <td>{row.sendCity}</td>

                                            <td>
                                                <label className="admin_checkbox" style={{ display: "inline-flex", alignItems: "center" }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={!!row.active}
                                                        onChange={(e) => patchRow(it.id, { active: e.target.checked })}
                                                    />
                                                    <span style={{ marginLeft: 8 }}>{row.active ? "Да" : "Нет"}</span>
                                                </label>
                                            </td>

                                            <td>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    step={1}
                                                    className="admin-input"
                                                    style={{ width: 140 }}
                                                    value={row.sum}
                                                    onChange={(e) => patchRow(it.id, { sum: Number(e.target.value) })}
                                                />
                                            </td>

                                            <td>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    step={1}
                                                    className="admin-input"
                                                    style={{ width: 140 }}
                                                    value={row.newSum ?? ""}
                                                    onChange={(e) => {
                                                        const v = e.target.value.trim();
                                                        patchRow(it.id, { newSum: v === "" ? null : Number(v) });
                                                    }}
                                                    placeholder="—"
                                                />
                                            </td>

                                            <td>
                                                <DatePicker
                                                    selected={fromISODate(row.firstApplyDate)}
                                                    onChange={(d: Date | null) => patchRow(it.id, { firstApplyDate: toISODate(d as Date | null) })}
                                                    dateFormat="dd.MM.yyyy"
                                                    className="admin-input w-140"
                                                    placeholderText="—"
                                                    calendarStartDay={1}
                                                    minDate={new Date(Date.now() + 24 * 60 * 60 * 1000)}
                                                />
                                            </td>

                                            <td>
                                                <div className="supplies-actions" style={{ gap: 8 }}>
                                                    <button
                                                        type="button"
                                                        className="admin-button-primary"
                                                        disabled={!dirty || saving}
                                                        onClick={() => saveRow(it.id)}
                                                    >
                                                        {saving ? (<BtnSpinner/>) : "Сохранить"}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="admin-button-secondary admin-button-secondary--ghost"
                                                        disabled={!dirty || saving}
                                                        onClick={() => resetRow(it.id)}
                                                    >
                                                        Отменить
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>
        </AdminLayout>
    );
};

export default AdminDirectionsPage;
